"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import {
	FaFacebook,
	FaInstagram,
	FaTwitter,
	FaLinkedin,
	FaCheckCircle,
	FaCopyright,
} from "react-icons/fa";

const FISCAL_CODE = "RO38297899";
const FOUNDED_YEAR = 2017;

export default function Footer() {
	const t = useTranslations("Footer");
	const tCommon = useTranslations("Common");
	const currentYear = new Date().getFullYear();
	const yearsOfActivity = Math.max(0, currentYear - FOUNDED_YEAR);

	return (
		<footer className="bg-slate-900 text-white text-[1.5rem]">
			<div className="w-full section-padding py-14">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
					{/* Column 1 – Brand */}
					<div className="flex flex-col">
						<Link
							href="/"
							className="inline-flex items-center no-underline text-white hover:opacity-90 transition-opacity w-max"
						>
							<Image
								src="/assets/business-development-together-logo.webp"
								alt={t("companyName")}
								width={120}
								height={40}
								className="h-8 w-auto object-contain object-left"
							/>
						</Link>
						<p className="mt-4 text-slate-300 leading-relaxed max-w-[28rem]">
							{t("tagline")}
						</p>
						<div className="flex gap-4 mt-6">
							<a
								href="https://www.facebook.com/businessdevelopmenttogether"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center justify-center w-11 h-11 rounded-lg border border-slate-500 text-white hover:border-white hover:text-bdt-gold transition-colors"
								aria-label={tCommon("facebook")}
							>
								<FaFacebook size={22} />
							</a>
							<a
								href="https://www.instagram.com/businessdevelopmenttogether/"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center justify-center w-11 h-11 rounded-lg border border-slate-500 text-white hover:border-white hover:text-bdt-gold transition-colors"
								aria-label={tCommon("instagram")}
							>
								<FaInstagram size={22} />
							</a>
							<a
								href="https://twitter.com/Busines79877466"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center justify-center w-11 h-11 rounded-lg border border-slate-500 text-white hover:border-white hover:text-bdt-gold transition-colors"
								aria-label={tCommon("twitter")}
							>
								<FaTwitter size={22} />
							</a>
							<a
								href="https://www.linkedin.com/company/business-development-together/"
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center justify-center w-11 h-11 rounded-lg border border-slate-500 text-white hover:border-white hover:text-bdt-gold transition-colors"
								aria-label={tCommon("linkedin")}
							>
								<FaLinkedin size={22} />
							</a>
						</div>
					</div>

					{/* Column 2 – Investments */}
					<div>
						<h3 className="font-bold text-white uppercase tracking-wide text-[1.4rem] mb-4">
							{t("investments")}
						</h3>
						<ul className="space-y-2">
							<li>
								<Link
									href="/investment#thesis"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("investmentThesis")}
								</Link>
							</li>
							<li>
								<Link
									href="/investment#strategy"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("strategy5Year")}
								</Link>
							</li>
							<li>
								<Link
									href="/investment#criteria"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("eligibilityCriteria")}
								</Link>
							</li>
						</ul>
					</div>

					{/* Column 3 – Group */}
					<div>
						<h3 className="font-bold text-white uppercase tracking-wide text-[1.4rem] mb-4">
							{t("group")}
						</h3>
						<ul className="space-y-2">
							<li>
								<Link
									href="/"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("aboutBdt")}
								</Link>
							</li>
							<li>
								<Link
									href="/working-together"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("softwareServices")}
								</Link>
							</li>
							<li>
								<Link
									href="/contact"
									className="text-slate-300 hover:text-white transition-colors no-underline"
								>
									{t("contact")}
								</Link>
							</li>
						</ul>
					</div>

					{/* Column 4 – Legal entity */}
					<div className="lg:col-span-1">
						<div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
							<h3 className="font-bold text-white flex items-center gap-2 text-[1.4rem] mb-4">
								<FaCheckCircle className="text-green-500 shrink-0" size={20} />
								{t("legalEntity")}
							</h3>
							<ul className="space-y-2 text-slate-300 text-[1.4rem]">
								<li>
									{t("poweredBy")}{" "}
									<Link
										href="/"
										className="text-white hover:text-bdt-gold no-underline"
									>
										{t("companyName")}
									</Link>
								</li>
								<li>{t("foundedAndActivity", { years: yearsOfActivity })}</li>
								<li>
									{t("fiscalCode")}:{" "}
									<span className="font-[Verdana,Geneva,Tahoma,sans-serif] text-white">
										{FISCAL_CODE}
									</span>
								</li>
								<li>
									{t("location")}: {t("locationValue")}
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>

			{/* Separator */}
			<div className="border-t border-slate-700 w-full" />

			{/* Bottom strip */}
			<div className="w-full section-padding py-5">
				<div className="flex flex-col sm:flex-row items-center justify-between gap-4">
					<p className="text-slate-400 text-[1.4rem] flex items-center gap-1">
						<FaCopyright className="shrink-0" size={14} />
						2017 - {currentYear} {t("companyName")}. {t("rights")}
					</p>
					<div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-[1.4rem]">
						<Link
							href="/privacy"
							className="text-slate-400 hover:text-white transition-colors no-underline"
						>
							{t("privacyPolicy")}
						</Link>
						<span className="text-slate-600" aria-hidden>
							|
						</span>
						<Link
							href="/cookie"
							className="text-slate-400 hover:text-white transition-colors no-underline"
						>
							{t("cookiePolicy")}
						</Link>
						<span className="text-slate-600" aria-hidden>
							|
						</span>
						<Link
							href="/accessibility"
							className="text-slate-400 hover:text-white transition-colors no-underline"
						>
							{t("accessibilityStatement")}
						</Link>
					</div>
				</div>
			</div>
		</footer>
	);
}
